import './i18n';
// frontend/src/App.js  (subdomain-aware routing)
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import { useHotelContext } from './store/hotelContext';
import { useServiceWorker } from './hooks/usePWA';
import { isOnSubdomain } from './utils/subdomain';
import { ConfirmDialog } from './components/ConfirmDialog';
import SplashScreen from './components/SplashScreen';
import PWAInstallBanner from './components/PWAInstallBanner';
import OfflineBanner from './components/OfflineBanner';

const ReviewPage = React.lazy(() => import('./pages/ReviewPage'));
const Login = React.lazy(() => import('./pages/Login'));
const Layout = React.lazy(() => import('./pages/Layout'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Reservations = React.lazy(() => import('./pages/Reservations'));
const Rooms = React.lazy(() => import('./pages/Rooms'));
const CheckInOut = React.lazy(() => import('./pages/CheckInOut'));
const BellDesk             = React.lazy(() => import('./pages/BellDesk'));
const DoormanScan          = React.lazy(() => import('./pages/DoormanScan'));
const ServiceRequestsAdmin = React.lazy(() => import('./pages/ServiceRequestsAdmin'));
const UpsellAdmin          = React.lazy(() => import('./pages/UpsellAdmin'));
const SlotBookingAdmin     = React.lazy(() => import('./pages/SlotBookingAdmin'));
const WhatsAppAdmin        = React.lazy(() => import('./pages/WhatsAppAdmin'));
const PreArrivalAdmin      = React.lazy(() => import('./pages/PreArrivalAdmin'));
const LateCheckoutAdmin    = React.lazy(() => import('./pages/LateCheckoutAdmin'));
const Guests = React.lazy(() => import('./pages/Guests'));
const Housekeeping = React.lazy(() => import('./pages/Housekeeping'));
const Restaurant = React.lazy(() => import('./pages/Restaurant'));
const BreakfastRegister = React.lazy(() => import('./pages/BreakfastRegister'));
const Packages          = React.lazy(() => import('./pages/Packages'));
const Coupons           = React.lazy(() => import('./pages/Coupons'));
const Maintenance = React.lazy(() => import('./pages/Maintenance'));
const Events = React.lazy(() => import('./pages/Events'));
const Spa = React.lazy(() => import('./pages/Spa'));
const Billing           = React.lazy(() => import('./pages/Billing'));
const CorporateBilling  = React.lazy(() => import('./pages/CorporateBilling'));
const Reports = React.lazy(() => import('./pages/Reports'));
const Inventory = React.lazy(() => import('./pages/Inventory'));
const Staff = React.lazy(() => import('./pages/Staff'));
const Settings = React.lazy(() => import('./pages/Settings'));
const SuperAdmin = React.lazy(() => import('./pages/SuperAdmin'));
const HotelNotFound = React.lazy(() => import('./pages/HotelNotFound'));
const ChannelManager = React.lazy(() => import('./pages/ChannelManager'));
const ApiKeys = React.lazy(() => import('./pages/ApiKeys'));
const BookingWidget = React.lazy(() => import('./pages/BookingWidget'));
const TaxSettings      = React.lazy(() => import('./pages/TaxSettings'));
const MushakPage       = React.lazy(() => import('./pages/MushakPage'));
const ForgotPassword   = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword    = React.lazy(() => import('./pages/ResetPassword'));
const InvoiceSettings  = React.lazy(() => import('./pages/InvoiceSettings'));
const Expenses        = React.lazy(() => import('./pages/Expenses'));
const Attendance         = React.lazy(() => import('./pages/Attendance'));
const FingerprintDevices = React.lazy(() => import('./pages/FingerprintDevices'));
const StaffZoneAccess    = React.lazy(() => import('./pages/StaffZoneAccess'));
const VehicleRegistry    = React.lazy(() => import('./pages/VehicleRegistry'));
const LuggageTracking    = React.lazy(() => import('./pages/LuggageTracking'));
const CashDrawer         = React.lazy(() => import('./pages/CashDrawer'));
const MyProfile          = React.lazy(() => import('./pages/MyProfile'));
const Approvals          = React.lazy(() => import('./pages/Approvals'));
const SmartLock          = React.lazy(() => import('./pages/SmartLock'));
const LeaveRequests      = React.lazy(() => import('./pages/LeaveRequests'));
const Payroll         = React.lazy(() => import('./pages/Payroll'));
const ShiftScheduler  = React.lazy(() => import('./pages/ShiftScheduler'));
const OwnerPortal     = React.lazy(() => import('./pages/OwnerPortal'));
const Facilities      = React.lazy(() => import('./pages/Facilities'));
const OutletPOS       = React.lazy(() => import('./pages/OutletPOS'));
const TableQRMenu     = React.lazy(() => import('./pages/TableQRMenu'));
const Activities      = React.lazy(() => import('./pages/Activities'));
const RevenueManager  = React.lazy(() => import('./pages/RevenueManager'));
const AuditTrail    = React.lazy(() => import('./pages/AuditTrail'));
const Subscription  = React.lazy(() => import('./pages/Subscription'));
const Backup          = React.lazy(() => import('./pages/Backup'));
const OutletManager   = React.lazy(() => import('./pages/OutletManager'));
const Laundry         = React.lazy(() => import('./pages/Laundry'));
const GroupBookings   = React.lazy(() => import('./pages/GroupBookings'));
const RoomingListPublic = React.lazy(() => import('./pages/RoomingListPublic'));
const AssetRegistry   = React.lazy(() => import('./pages/AssetRegistry'));
const Accounting      = React.lazy(() => import('./pages/Accounting'));
const GuestApp        = React.lazy(() => import('./pages/GuestApp'));
const GuestAppAdmin   = React.lazy(() => import('./pages/GuestAppAdmin'));
const GuestSignPage   = React.lazy(() => import('./pages/GuestSignPage'));
const Requisitions    = React.lazy(() => import('./pages/Requisitions'));
const KPI             = React.lazy(() => import('./pages/KPI'));
const HRModule        = React.lazy(() => import('./pages/HRModule'));
const MiniBarMgmt     = React.lazy(() => import('./pages/MiniBar'));
const Kitchen         = React.lazy(() => import('./pages/Kitchen'));
const KitchenAdmin    = React.lazy(() => import('./pages/KitchenAdmin'));
const NightAudit      = React.lazy(() => import('./pages/NightAudit'));
const LobbyDisplay    = React.lazy(() => import('./pages/LobbyDisplay'));
const SSOExchange     = React.lazy(() => import('./pages/SSOExchange'));
import MaintenancePage from './pages/MaintenancePage';
const MarketingLanding    = React.lazy(() => import('./pages/MarketingLanding'));
const PricingPage         = React.lazy(() => import('./pages/PricingPage'));
const SupportPortal       = React.lazy(() => import('./pages/SupportPortal'));
const ContactPage         = React.lazy(() => import('./pages/ContactPage'));
const DemoBookingPage     = React.lazy(() => import('./pages/DemoBookingPage'));
const StartTrialPage      = React.lazy(() => import('./pages/StartTrialPage'));
const FeaturesPage        = React.lazy(() => import('./pages/FeaturesPage'));
const GuestAppPage        = React.lazy(() => import('./pages/GuestAppPage'));
const AIFeaturesPage      = React.lazy(() => import('./pages/AIFeaturesPage'));
const HRFeaturesPage      = React.lazy(() => import('./pages/HRFeaturesPage'));
const BellDeskPage        = React.lazy(() => import('./pages/BellDeskPage'));
const AboutPage           = React.lazy(() => import('./pages/AboutPage'));
const BookingFeaturesPage = React.lazy(() => import('./pages/BookingFeaturesPage'));
const MarketingFeaturesPage = React.lazy(() => import('./pages/MarketingFeaturesPage'));
const AccountingFeaturesPage = React.lazy(() => import('./pages/AccountingFeaturesPage'));
const ChannelFeaturesPage    = React.lazy(() => import('./pages/ChannelFeaturesPage'));
const PrivacyPage       = React.lazy(() => import('./pages/PrivacyPage'));
const TermsPage         = React.lazy(() => import('./pages/TermsPage'));
const CookiePolicyPage  = React.lazy(() => import('./pages/CookiePolicyPage'));
const BlogPage      = React.lazy(() => import('./pages/BlogPage'));
const GetQuotePage  = React.lazy(() => import('./pages/GetQuotePage'));
const DocsPage           = React.lazy(() => import('./pages/DocsPage'));
const SystemStatusPage   = React.lazy(() => import('./pages/SystemStatusPage'));
const GeoLandingPage = React.lazy(() => import('./pages/GeoLandingPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      gcTime: 300000,
      // 'offlineFirst' makes React Query always attempt the fetch even when offline,
      // so the Service Worker can serve cached responses instead of pausing the query.
      networkMode: 'offlineFirst',
      retry: (failureCount, error) => {
        // Don't retry when offline — fail fast so pages show the offline state quickly
        if (!navigator.onLine) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

const MKT_LOADING = {
  en: 'Loading StayNivo…',
  bn: 'StayNivo লোড হচ্ছে…',
  fr: 'Chargement de StayNivo…',
  ar: 'جارٍ تحميل StayNivo…',
};

const Loader = ({ text }) => {
  const { t } = useTranslation();
  const mktLang = localStorage.getItem('mkt-lang') || 'en';
  const defaultText = MKT_LOADING[mktLang] || MKT_LOADING.en;
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0b1120', color:'#d4a853', fontFamily:'sans-serif', gap:10 }}>
      <span>{text || t('app.loading', defaultText)}</span>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const ModuleRoute = ({ module, modules: modulesArr, children }) => {
  const { hotel } = useHotelContext();
  // hotel loads async — while loading, render children to avoid false redirect
  if (!hotel) return children;
  const mods = hotel.modules || {};
  const keys = modulesArr || (module ? [module] : []);
  const allowed = keys.every(k => mods[k] !== false);
  return allowed ? children : <Navigate to="/dashboard" replace />;
}

function RequireRole({ roles, children }) {
  const { user } = useAuthStore();
  if (!roles || roles.includes(user?.role)) return children;
  return <Navigate to="/dashboard" replace />;
};

function defaultHomeFor(role) {
  if (role === 'waiter') return '/outlets';
  return '/dashboard';
}

// Minimum ms the splash must stay visible so the animation completes.
const SPLASH_MIN_MS = 2600;

// ─── Maintenance gate ────────────────────────────────────────────────────────
function useMaintenanceGate() {
  const { pathname } = useLocation();
  const [status, setStatus] = useState(null); // null=loading, {active,message}
  const [bypassed, setBypassed] = useState(
    () => !!localStorage.getItem('sn-maint-bypass') || !!localStorage.getItem('staynivo-superadmin-token')
  );

  // Always let /superadmin through without even fetching
  const isSuperAdminRoute = pathname.startsWith('/superadmin');

  useEffect(() => {
    if (isSuperAdminRoute || bypassed) { setStatus({ active: false }); return; }
    fetch('/api/public/maintenance')
      .then(r => r.json())
      .then(d => setStatus(d.data || { active: false }))
      .catch(() => setStatus({ active: false }));
  }, [isSuperAdminRoute, bypassed]);

  return { status, bypassed, onUnlocked: () => setBypassed(true) };
}

// ─── Main domain: super admin + owner portal ──────────────────────────────────
// Defined OUTSIDE AppContent so its identity is stable across re-renders.
// This guarantees SuperAdmin never unmounts due to hotel/splash state changes.
function MainDomainApp() {
  const { status, onUnlocked } = useMaintenanceGate();

  if (!status) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'#0b1120' }}/>
    );
  }
  if (status.active) {
    return <MaintenancePage message={status.message} onUnlocked={onUnlocked} />;
  }

  return (
    <>
    <OfflineBanner />
    <Routes>
      {/* Marketing public pages */}
      <Route path="/"                      element={<React.Suspense fallback={<Loader />}><MarketingLanding /></React.Suspense>} />
      <Route path="/pricing"               element={<React.Suspense fallback={<Loader />}><PricingPage /></React.Suspense>} />
      <Route path="/support"               element={<React.Suspense fallback={<Loader />}><SupportPortal /></React.Suspense>} />
      <Route path="/contact"               element={<React.Suspense fallback={<Loader />}><ContactPage /></React.Suspense>} />
      <Route path="/demo"                  element={<React.Suspense fallback={<Loader />}><DemoBookingPage /></React.Suspense>} />
      <Route path="/start-trial"           element={<React.Suspense fallback={<Loader />}><StartTrialPage /></React.Suspense>} />
      <Route path="/free-trial"            element={<React.Suspense fallback={<Loader />}><StartTrialPage /></React.Suspense>} />
      <Route path="/features"              element={<React.Suspense fallback={<Loader />}><FeaturesPage /></React.Suspense>} />
      <Route path="/features/guest-app"    element={<React.Suspense fallback={<Loader />}><GuestAppPage /></React.Suspense>} />
      <Route path="/features/ai"           element={<React.Suspense fallback={<Loader />}><AIFeaturesPage /></React.Suspense>} />
      <Route path="/features/hr"           element={<React.Suspense fallback={<Loader />}><HRFeaturesPage /></React.Suspense>} />
      <Route path="/features/bell-desk"    element={<React.Suspense fallback={<Loader />}><BellDeskPage /></React.Suspense>} />
      <Route path="/features/accounting"   element={<React.Suspense fallback={<Loader />}><AccountingFeaturesPage /></React.Suspense>} />
      <Route path="/features/channels"     element={<React.Suspense fallback={<Loader />}><ChannelFeaturesPage /></React.Suspense>} />
      <Route path="/features/bookings"     element={<React.Suspense fallback={<Loader />}><BookingFeaturesPage /></React.Suspense>} />
      <Route path="/features/marketing"    element={<React.Suspense fallback={<Loader />}><MarketingFeaturesPage /></React.Suspense>} />
      <Route path="/about"                 element={<React.Suspense fallback={<Loader />}><AboutPage /></React.Suspense>} />
      <Route path="/privacy"               element={<React.Suspense fallback={<Loader />}><PrivacyPage /></React.Suspense>} />
      <Route path="/terms"                 element={<React.Suspense fallback={<Loader />}><TermsPage /></React.Suspense>} />
      <Route path="/cookie-policy"         element={<React.Suspense fallback={<Loader />}><CookiePolicyPage /></React.Suspense>} />
      <Route path="/blog"                  element={<React.Suspense fallback={<Loader />}><BlogPage /></React.Suspense>} />
      <Route path="/blog/:slug"            element={<React.Suspense fallback={<Loader />}><BlogPage /></React.Suspense>} />
      <Route path="/get-quote"             element={<React.Suspense fallback={<Loader />}><GetQuotePage /></React.Suspense>} />
      <Route path="/docs"                  element={<React.Suspense fallback={<Loader />}><DocsPage /></React.Suspense>} />
      <Route path="/status"               element={<React.Suspense fallback={<Loader />}><SystemStatusPage /></React.Suspense>} />
      {/* Geo-targeted SEO landing pages */}
      <Route path="/bangladesh"   element={<React.Suspense fallback={<Loader />}><GeoLandingPage geoKey="bangladesh" /></React.Suspense>} />
      <Route path="/saudi-arabia" element={<React.Suspense fallback={<Loader />}><GeoLandingPage geoKey="saudi-arabia" /></React.Suspense>} />
      <Route path="/uae"          element={<React.Suspense fallback={<Loader />}><GeoLandingPage geoKey="uae" /></React.Suspense>} />
      <Route path="/middle-east"  element={<React.Suspense fallback={<Loader />}><GeoLandingPage geoKey="middle-east" /></React.Suspense>} />
      <Route path="/qatar"        element={<React.Suspense fallback={<Loader />}><GeoLandingPage geoKey="qatar" /></React.Suspense>} />
      <Route path="/kuwait"       element={<React.Suspense fallback={<Loader />}><GeoLandingPage geoKey="kuwait" /></React.Suspense>} />
      {/* Admin + portal */}
      <Route path="/superadmin"    element={<React.Suspense fallback={<Loader />}><SuperAdmin /></React.Suspense>} />
      <Route path="/superadmin/*"  element={<React.Suspense fallback={<Loader />}><SuperAdmin /></React.Suspense>} />
      <Route path="/owner-portal"  element={<React.Suspense fallback={<Loader />}><OwnerPortal /></React.Suspense>} />
      <Route path="/forgot-password" element={<React.Suspense fallback={<Loader />}><ForgotPassword /></React.Suspense>} />
      <Route path="/reset-password"  element={<React.Suspense fallback={<Loader />}><ResetPassword /></React.Suspense>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

// ─── Hotel subdomain: full HMS with splash screen ────────────────────────────
// Splash state lives here and ONLY here — cannot affect MainDomainApp.
function HotelApp({ hotel, isLoading }) {
  const { isAuthenticated, user } = useAuthStore();
  const { pathname } = useLocation();
  const isWidget = pathname === '/book';
  const [splashLeaving, setSplashLeaving] = useState(false);
  const [splashGone,    setSplashGone]    = useState(false);
  const splashStartRef = React.useRef(Date.now());

  useEffect(() => {
    if (!isLoading && !splashGone) {
      const elapsed = Date.now() - splashStartRef.current;
      const holdMs  = Math.max(0, SPLASH_MIN_MS - elapsed);
      const t1 = setTimeout(() => setSplashLeaving(true), holdMs);
      const t2 = setTimeout(() => setSplashGone(true),   holdMs + 700);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [isLoading]);

  return (
    <>
      {!splashGone && <SplashScreen hotel={hotel} leaving={splashLeaving} />}
      <React.Suspense fallback={<Loader />}>
      {!isWidget && <PWAInstallBanner />}
      {!isWidget && <OfflineBanner />}
      <Routes>
      <Route path="/sso" element={<SSOExchange />} />
      <Route path="/review/:token" element={<ReviewPage />} />
      <Route path="/rooming-list/:token" element={<RoomingListPublic />} />
      {/* Public booking widget — embeddable via iframe: /book?key=sk_live_... */}
      <Route path="/book" element={<BookingWidget />} />
      {/* Guest Room App — standalone PWA at /guest (no staff auth needed) */}
      <Route path="/guest/*" element={<GuestApp />} />
      {/* Guest signing page — no auth, accessed via QR / tablet link */}
      <Route path="/guest-sign/:token" element={<React.Suspense fallback={<Loader />}><GuestSignPage /></React.Suspense>} />
      {/* QR contactless menu — public, no auth needed */}
      <Route path="/table/:token" element={<React.Suspense fallback={<Loader />}><TableQRMenu /></React.Suspense>} />
      <Route path="/login" element={isAuthenticated ? <Navigate to={defaultHomeFor(user?.role)} /> : <Login />} />
      {/* Lobby display — full-screen, no layout wrapper */}
      <Route path="/bell-desk/lobby" element={<LobbyDisplay />} />
      <Route path="/" element={<Navigate to={isAuthenticated ? defaultHomeFor(user?.role) : '/login'} />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="reservations" element={<RequireRole roles={['admin', 'manager', 'receptionist', 'accountant']}><Reservations /></RequireRole>} />
        <Route path="rooms" element={<RequireRole roles={['admin', 'manager', 'receptionist', 'housekeeping']}><Rooms /></RequireRole>} />
        <Route path="checkin" element={<RequireRole roles={['admin', 'manager', 'receptionist']}><CheckInOut /></RequireRole>} />
        <Route path="bell-desk"         element={<RequireRole roles={['admin','manager','receptionist','bell_captain','bellboy','security_staff']}><BellDesk /></RequireRole>} />
        <Route path="bell-desk/doorman" element={<RequireRole roles={['admin','manager','receptionist','bell_captain','bellboy','security_staff']}><DoormanScan /></RequireRole>} />
        <Route path="service-requests"  element={<RequireRole roles={['admin','manager','receptionist','housekeeping','concierge_staff','maintenance']}><ServiceRequestsAdmin /></RequireRole>} />
        <Route path="upsell"            element={<RequireRole roles={['admin','manager','receptionist']}><UpsellAdmin /></RequireRole>} />
        <Route path="packages"          element={<RequireRole roles={['admin','manager','receptionist','restaurant','spa_staff','f_and_b_manager']}><Packages /></RequireRole>} />
        <Route path="coupons"           element={<RequireRole roles={['admin','manager']}><Coupons /></RequireRole>} />
        <Route path="slot-booking"      element={<RequireRole roles={['admin','manager','receptionist','spa_staff','f_and_b_manager']}><SlotBookingAdmin /></RequireRole>} />
        <Route path="whatsapp"          element={<RequireRole roles={['admin','manager']}><WhatsAppAdmin /></RequireRole>} />
        <Route path="pre-arrival"       element={<RequireRole roles={['admin','manager','receptionist']}><PreArrivalAdmin /></RequireRole>} />
        <Route path="late-checkout"     element={<RequireRole roles={['admin','manager','receptionist']}><LateCheckoutAdmin /></RequireRole>} />
        <Route path="guests" element={<RequireRole roles={['admin', 'manager', 'receptionist', 'accountant']}><Guests /></RequireRole>} />
        <Route path="housekeeping" element={<RequireRole roles={['admin', 'manager', 'housekeeping']}><ModuleRoute module="housekeeping"><Housekeeping /></ModuleRoute></RequireRole>} />
        <Route path="restaurant" element={<RequireRole roles={['admin', 'manager', 'restaurant']}><ModuleRoute module="restaurant"><Restaurant /></ModuleRoute></RequireRole>} />
        <Route path="breakfast-register" element={<RequireRole roles={['admin', 'manager', 'receptionist', 'restaurant']}><ModuleRoute module="restaurant"><BreakfastRegister /></ModuleRoute></RequireRole>} />
        <Route path="maintenance" element={<RequireRole roles={['admin', 'manager', 'maintenance']}><ModuleRoute module="maintenance"><Maintenance /></ModuleRoute></RequireRole>} />
        <Route path="events" element={<RequireRole roles={['admin', 'manager']}><ModuleRoute module="events"><Events /></ModuleRoute></RequireRole>} />
        <Route path="spa" element={<RequireRole roles={['admin', 'manager']}><ModuleRoute module="spa"><Spa /></ModuleRoute></RequireRole>} />
        <Route path="billing" element={<RequireRole roles={['admin', 'manager', 'accountant', 'supervisor', 'receptionist']}><ModuleRoute module="billing"><Billing /></ModuleRoute></RequireRole>} />
        <Route path="corporate" element={<RequireRole roles={['admin', 'manager', 'accountant', 'receptionist']}><ModuleRoute module="corporate_billing"><CorporateBilling /></ModuleRoute></RequireRole>} />
        <Route path="reports" element={<RequireRole roles={['admin', 'manager', 'accountant']}><ModuleRoute module="reports"><Reports /></ModuleRoute></RequireRole>} />
        <Route path="inventory" element={<RequireRole roles={['admin', 'manager', 'housekeeping']}><ModuleRoute module="inventory"><Inventory /></ModuleRoute></RequireRole>} />
        <Route path="staff" element={<RequireRole roles={['admin', 'manager']}><ModuleRoute module="staff"><Staff /></ModuleRoute></RequireRole>} />
        <Route path="settings" element={<Settings />} />
        <Route path="channels" element={<RequireRole roles={['admin', 'manager']}><ChannelManager /></RequireRole>} />
        <Route path="api-keys" element={<RequireRole roles={['admin']}><ApiKeys /></RequireRole>} />
        <Route path="tax" element={<RequireRole roles={['admin', 'manager', 'accountant']}><TaxSettings /></RequireRole>} />
        <Route path="tax/mushak" element={<RequireRole roles={['admin', 'manager', 'accountant']}><MushakPage /></RequireRole>} />
        <Route path="invoice-settings" element={<RequireRole roles={['admin', 'manager', 'accountant']}><InvoiceSettings /></RequireRole>} />
        <Route path="expenses"       element={<ModuleRoute module="hr"><Expenses /></ModuleRoute>} />
        <Route path="attendance"          element={<ModuleRoute module="hr"><Attendance /></ModuleRoute>} />
        <Route path="biometric-devices"   element={<ModuleRoute module="hr"><FingerprintDevices /></ModuleRoute>} />
        <Route path="zone-access"         element={<RequireRole roles={['admin','manager','security_staff']}><StaffZoneAccess /></RequireRole>} />
        <Route path="vehicle-registry"    element={<RequireRole roles={['admin','manager','receptionist','supervisor','security_staff']}><VehicleRegistry /></RequireRole>} />
        <Route path="luggage-tracking"    element={<ModuleRoute module="luggage_tracking"><RequireRole roles={['admin','manager','receptionist','bell_captain','bellboy','supervisor']}><LuggageTracking /></RequireRole></ModuleRoute>} />
        <Route path="cash-drawer"         element={<RequireRole roles={['admin','manager','supervisor','receptionist','accountant']}><CashDrawer /></RequireRole>} />
        <Route path="my-profile"           element={<MyProfile />} />
        <Route path="approvals"            element={<Approvals />} />
        <Route path="smart-lock"          element={<RequireRole roles={['admin','manager']}><SmartLock /></RequireRole>} />
        <Route path="leave"          element={<ModuleRoute module="hr"><LeaveRequests /></ModuleRoute>} />
        <Route path="payroll"        element={<ModuleRoute module="hr"><Payroll /></ModuleRoute>} />
        <Route path="shifts"         element={<ModuleRoute module="staff"><ShiftScheduler /></ModuleRoute>} />
        <Route path="facilities"     element={<ModuleRoute module="facilities"><Facilities /></ModuleRoute>} />
        <Route path="outlets"        element={<ModuleRoute module="outlets"><OutletPOS /></ModuleRoute>} />
        <Route path="laundry"        element={<ModuleRoute module="laundry"><Laundry /></ModuleRoute>} />
        <Route path="outlet-manager" element={<RequireRole roles={['admin', 'manager']}><ModuleRoute module="outlets"><OutletManager /></ModuleRoute></RequireRole>} />
        <Route path="activities"     element={<ModuleRoute module="activities"><Activities /></ModuleRoute>} />
        <Route path="revenue"        element={<ModuleRoute module="revenue"><RevenueManager /></ModuleRoute>} />
        <Route path="audit-trail"    element={<AuditTrail />} />
        <Route path="subscription"   element={<Subscription />} />
        <Route path="backup"         element={<Backup />} />
        <Route path="groups"         element={<ModuleRoute module="group_bookings"><GroupBookings /></ModuleRoute>} />
        <Route path="asset-registry" element={<ModuleRoute module="asset_registry"><AssetRegistry /></ModuleRoute>} />
        <Route path="accounting"    element={<ModuleRoute module="accounting"><Accounting /></ModuleRoute>} />
        <Route path="night-audit"   element={<RequireRole roles={['admin','manager','accountant']}><NightAudit /></RequireRole>} />
        <Route path="requisitions" element={<ModuleRoute module="requisitions"><Requisitions /></ModuleRoute>} />
        <Route path="kpi" element={<ModuleRoute module="kpi"><KPI /></ModuleRoute>} />
        <Route path="hr-module" element={<RequireRole roles={['admin','manager']}><ModuleRoute module="hr"><HRModule /></ModuleRoute></RequireRole>} />
        <Route path="guest-app" element={<RequireRole roles={['admin', 'manager', 'supervisor']}><ModuleRoute modules={['room_app','housekeeping']}><GuestAppAdmin /></ModuleRoute></RequireRole>} />
        <Route path="minibar"         element={<ModuleRoute modules={['room_app','housekeeping']}><MiniBarMgmt /></ModuleRoute>} />
        <Route path="kitchen"         element={<ModuleRoute module="kitchen"><Kitchen /></ModuleRoute>} />
        <Route path="kitchen-admin"   element={<RequireRole roles={['admin','manager','f_and_b_manager']}><ModuleRoute module="kitchen"><KitchenAdmin /></ModuleRoute></RequireRole>} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password"  element={<ResetPassword />} />
      </Route>
      <Route path="*" element={<Navigate to={defaultHomeFor(user?.role)} />} />
    </Routes>
    </React.Suspense>
    </>
  );
}

// ─── AppContent: decides which app to render ─────────────────────────────────
function AppContent() {
  const { accessToken, setToken } = useAuthStore();
  const { loadHotelFromSubdomain, notFound, isLoading, hotel } = useHotelContext();
  useServiceWorker();

  useEffect(() => { if (accessToken) setToken(accessToken); }, []);
  useEffect(() => { loadHotelFromSubdomain(); }, []);

  if (notFound) return <React.Suspense fallback={<Loader />}><HotelNotFound /></React.Suspense>;

  if (!isOnSubdomain()) return <MainDomainApp />;

  return <HotelApp hotel={hotel} isLoading={isLoading} />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <React.Suspense fallback={<Loader />}>
          <AppContent />
        </React.Suspense>
        <ConfirmDialog />
        <Toaster position="top-right" toastOptions={{
          duration: 4000,
          style: { background:'#1a2236', color:'#f0f4ff', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, fontSize:13 },
          success: { iconTheme: { primary:'#22c55e', secondary:'#0b1120' } },
          error: { iconTheme: { primary:'#f43f5e', secondary:'#0b1120' } },
        }} />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
